import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { ToastNotificationComponent } from '../shared/toast-notification/toast-notification.component';
import { InAppBrowserService } from './in-app-browser.service';
import { ConfigService } from './json-config.service';
import { LoggerService } from './logger/logger.service';
import { PosthogService } from './posthog.service';
import { RestApiService } from './rest-api/rest-api.service';
import { ToastNotificationService } from './toast-notification/toast-notification.service';
import { UserNotificationService } from './user-notification/user-notification.service';
import { UserService } from './user/user.service';
import { UserTypes } from './rest-api/rest-api';
import { Subscription } from 'rxjs';
import { PushNotificationsService } from './push-notifications.service';
import { ClientConsultService } from './client-consult.service';
import { take, tap } from 'rxjs/operators';
import { parseToError } from '../shared/functions-angular';
import { NativeCheckService } from './native-check.service';

interface LogoutInput {
  idToken?: string,
  displayErrorOnRedirectBack?: UserTypes
  idleLogoutMessage?: boolean
  userType?: UserTypes,
}

interface LogoutOptions {
  withMessage?: 'idleLogoutMessage',
  userType?: UserTypes,
}

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  private _localAppUrl = '';

  public get localAppUrl(): string {
    const origin = window?.location?.origin;
    if(origin) {
      this._localAppUrl = origin + '/login';
    }

    return this._localAppUrl;
  }

  private browserClose$: null | Subscription = null;
  private urlChange$: null | Subscription = null;

  public constructor(
    private userService: UserService,
    private restApiService: RestApiService,
    private userNotificationService: UserNotificationService,
    private configService: ConfigService,
    private browserService: InAppBrowserService,
    private posthogService: PosthogService,
    private router: Router,
    private zone: NgZone,
    private loggerService: LoggerService,
    private toastService: ToastNotificationService,
    private pushNotificationsService: PushNotificationsService,
    private clientConsultService: ClientConsultService,
    private nativeCheckService: NativeCheckService,
  ) {}

  public logOut(options?: LogoutOptions): void {
    if(this.nativeCheckService.isNativePlatform() && this.userService.loggedUser) {
      this.pushNotificationsService.deletePushNotificationsToken();
    }
    this.restApiService.logoutBackend().subscribe({
      next: (data) => {
        this.finishLogout({
          idleLogoutMessage: !!options?.withMessage,
          userType: options?.userType,
          ...data
        });
      },
      error: (error: unknown) => {
        const parsedError = parseToError(error);

        if(parsedError && parsedError.status !== 401 && parsedError.status !== 403) {
          this.loggerService.error('Error during logout process: ', error);
        }
        this.finishLogout({idleLogoutMessage: !!options?.withMessage});
      },
    });
  }

  public finishLogout(options?: LogoutInput): void {
    if(this.userNotificationService.bellSubscription) {
      this.userNotificationService.bellSubscription.unsubscribe();
    }

    this.userService.removeUserFromLocal().pipe(
      take(1),
      tap(() => {
        this.posthogService.endLog();
        this.clientConsultService.clearConsult();

        let keycloakLogoutUrl = `${this.configService.getConfig(EnvironmentKeys.oauthUrl)}/logout?client_id=${this.configService.getConfig(EnvironmentKeys.clientId)}`;

        // params for KeyCloak
        if(options?.idToken) {
          keycloakLogoutUrl = keycloakLogoutUrl + '&id_token_hint=' + options.idToken;
        }

        keycloakLogoutUrl += '&post_logout_redirect_uri=' + this.localAppUrl;

        // params for BWM on route back
        if(options) {
          let paramSeparator = '?';
          if(options.displayErrorOnRedirectBack) {
            keycloakLogoutUrl += paramSeparator + 'showNoBwmError=' + (options.userType ?? options?.displayErrorOnRedirectBack ?? 'client') as UserTypes;
            paramSeparator = '&';
          }
          if(options.idleLogoutMessage) {
            keycloakLogoutUrl += paramSeparator + 'idleLogout=true';
            paramSeparator = '&';
          }
        }

        if(!this.nativeCheckService.isNativePlatform()) {
          window.location.href = keycloakLogoutUrl;
        } else {
          this.handleNativeBrowser(keycloakLogoutUrl);
        }
      })
    ).subscribe();
  }

  private handleNativeBrowser(keycloakLogoutUrl: string): void {
    this.browserService.open(keycloakLogoutUrl);

    if(!this.browserService.networkConnected) {
      this.browserService.close();
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.browserLogoutLoadError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return;
    }

    this.urlChange$ = this.browserService.onUrlChange().subscribe((navUrl) => {
      // try to fix BI-409 todo - remove by better approach or getting host directly from Capacitor
      const staticProdNativeAppLogoutLandpage = 'https://app.bwm.bcas.cz/login';

      if (navUrl.startsWith(this.localAppUrl) || navUrl === staticProdNativeAppLogoutLandpage) {
        this.zone.run(() => {
          this.router.navigate(['/login']);
        });

        this.browserService.close();
        this.unsubBrowser();
      }
    });

    this.browserClose$ = this.browserService.onBrowserClose().subscribe(() => {
      this.unsubBrowser();
    });
  }

  private unsubBrowser(): void {
    this.browserClose$?.unsubscribe();
    this.urlChange$?.unsubscribe();
    this.browserClose$ = null;
    this.urlChange$ = null;
  }
}
